import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import type { ReasonCode, RuleOut } from '@/api/types';
import { Button } from '@/components/ui/button';
import type {
  ApprovalReasonFilter,
  OutcomeFilter,
  StatusFilter,
} from '@/hooks/useFindingsReview';
import type { InvoiceFilter } from '@/hooks/useTransactionDocuments';
import { APPROVAL_REASONS_ORDERED, APPROVAL_REASON_META } from '@/lib/approval-reasons';
import { SEARCH_QUERY_MAX_LENGTH } from '@/lib/findingSearch';
import { REASON_CODES_ORDERED, REASON_CODE_META } from '@/lib/reason-codes';
import { cn } from '@/lib/utils';

export type BnFilter = '' | 'has' | 'missing';

function FindingFilterBar({
  ruleFilter,
  setRuleFilter,
  provinceFilter,
  setProvinceFilter,
  supplierFilter,
  setSupplierFilter,
  transactionTypeFilter,
  setTransactionTypeFilter,
  classFilter,
  setClassFilter,
  glAccountFilter,
  setGlAccountFilter,
  currencyFilter,
  setCurrencyFilter,
  outcomeFilter,
  setOutcomeFilter,
  statusFilter,
  setStatusFilter,
  approvalReasonFilter,
  setApprovalReasonFilter,
  bnFilter,
  setBnFilter,
  bnLookupFailed,
  searchQuery,
  setSearchQuery,
  amountMin,
  setAmountMin,
  amountMax,
  setAmountMax,
  effRateMin,
  setEffRateMin,
  effRateMax,
  setEffRateMax,
  reasonCodesFilter,
  setReasonCodesFilter,
  invoiceFilter,
  setInvoiceFilter,
  invoiceLookupFailed,
  sourceFilter,
  setSourceFilter,
  sources,
  ledgerSourceFilter,
  setLedgerSourceFilter,
  ledgerSources,
  ruleIds,
  ruleById,
  provinces,
  suppliers,
  transactionTypes,
  classes,
  currencies,
  totalCount,
  showingCount,
  hasActiveFilter,
  onClearAll,
}: {
  ruleFilter: string;
  setRuleFilter: (v: string) => void;
  provinceFilter: string;
  setProvinceFilter: (v: string) => void;
  supplierFilter: string;
  setSupplierFilter: (v: string) => void;
  transactionTypeFilter: string;
  setTransactionTypeFilter: (v: string) => void;
  /** Optional — NetSuite Class filter (INP-407). Rendered only when both
   *  are provided AND the run surfaces at least one class. */
  classFilter?: string;
  setClassFilter?: (v: string) => void;
  /** Optional — substring match on `evidence['gl_account.name']`. The
   *  audit-log + supplier-scoped views skip the input. */
  glAccountFilter?: string;
  setGlAccountFilter?: (v: string) => void;
  /** Optional — the audit-log + supplier-scoped views skip the
   *  Currency dropdown. The Review queue passes both to enable it. */
  currencyFilter?: string;
  setCurrencyFilter?: (v: string) => void;
  /** Optional — recovery-opportunity vs compliance-risk narrowing. Lives in
   *  the primary row (it's a work-mode slicer like Status); the audit-log +
   *  supplier-scoped views skip it. */
  outcomeFilter?: OutcomeFilter;
  setOutcomeFilter?: (v: OutcomeFilter) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (v: StatusFilter) => void;
  /** Optional — approval-basis filter, rendered only when both are provided
   *  (like the reason-codes + invoice filters). */
  approvalReasonFilter?: ApprovalReasonFilter;
  setApprovalReasonFilter?: (v: ApprovalReasonFilter) => void;
  /** Optional — supplier BN-presence filter. Only the Suppliers tab
   *  passes these; other surfaces skip the dropdown. */
  bnFilter?: BnFilter;
  setBnFilter?: (v: BnFilter) => void;
  /** The BN lookup errored — disable the dropdown with a warning so a failed
   *  fetch can't read as "filter applied". */
  bnLookupFailed?: boolean;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  /** Optional — when both `amountMin`/`setAmountMin` and
   *  `amountMax`/`setAmountMax` are provided, render the amount range
   *  input. The audit-log + supplier-scoped views skip it. */
  amountMin?: string;
  setAmountMin?: (v: string) => void;
  amountMax?: string;
  setAmountMax?: (v: string) => void;
  /** Optional — when both `effRateMin`/`setEffRateMin` and the max pair
   *  are provided, render the effective-rate (%) range input. */
  effRateMin?: string;
  setEffRateMin?: (v: string) => void;
  effRateMax?: string;
  setEffRateMax?: (v: string) => void;
  /** Optional — when both are provided, render the reason-codes filter
   *  dropdown. Only the Review-queue surface passes these today. */
  reasonCodesFilter?: ReasonCode[];
  setReasonCodesFilter?: (v: ReasonCode[]) => void;
  /** Optional — three-state has-invoice filter. Only the Review-queue
   *  surface passes these today. */
  invoiceFilter?: InvoiceFilter;
  setInvoiceFilter?: (v: InvoiceFilter) => void;
  /** The link lookup errored — the select disables with a warning so a
   *  failed fetch can't read as "everything is unattached". */
  invoiceLookupFailed?: boolean;
  /** Optional — expense-source filter (dext | brex | ramp | direct).
   *  Rendered only when both + a non-empty `sources` list are provided
   *  (the review surfaces). INP-775. */
  sourceFilter?: string;
  setSourceFilter?: (v: string) => void;
  sources?: string[];
  /** Optional — ledger-source filter (xero | qbo | netsuite). Rendered
   *  only when both + a non-empty `ledgerSources` list are provided. INP-773. */
  ledgerSourceFilter?: string;
  setLedgerSourceFilter?: (v: string) => void;
  ledgerSources?: string[];
  ruleIds: string[];
  ruleById: Map<string, RuleOut>;
  provinces: string[];
  suppliers: string[];
  transactionTypes: string[];
  /** Only used when `setClassFilter` is provided. */
  classes?: string[];
  /** Only used when `setCurrencyFilter` is provided. */
  currencies?: string[];
  totalCount: number;
  showingCount: number;
  hasActiveFilter: boolean;
  onClearAll: () => void;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  // Count of active ADVANCED filters (everything not in the primary row)
  // so the collapsed "More filters" toggle advertises that filters are
  // hidden. Search/Status/Invoice live in the always-visible primary row
  // and are excluded.
  const advancedActiveCount =
    (ruleFilter ? 1 : 0) +
    (provinceFilter ? 1 : 0) +
    (supplierFilter ? 1 : 0) +
    (transactionTypeFilter ? 1 : 0) +
    (classFilter ? 1 : 0) +
    ((glAccountFilter ?? '').trim() ? 1 : 0) +
    (currencyFilter ? 1 : 0) +
    (sourceFilter ? 1 : 0) +
    (ledgerSourceFilter ? 1 : 0) +
    (amountMin || amountMax ? 1 : 0) +
    (effRateMin || effRateMax ? 1 : 0) +
    (bnFilter ? 1 : 0) +
    ((reasonCodesFilter?.length ?? 0) > 0 ? 1 : 0);

  const searchField = (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">Search</span>
      <div className="relative">
        <input
          type="search"
          value={searchQuery}
          onChange={(ev) => setSearchQuery(ev.target.value)}
          maxLength={SEARCH_QUERY_MAX_LENGTH}
          placeholder="GL, memo, supplier, tx id…"
          className="min-w-56 rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
        />
        {searchQuery ? (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-brand-slate hover:text-brand-royal"
            aria-label="Clear search"
            title="Clear search"
          >
            ✕
          </button>
        ) : null}
      </div>
    </label>
  );

  const statusField = (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">Status</span>
      <select
        value={statusFilter}
        onChange={(ev) => setStatusFilter(ev.target.value as StatusFilter)}
        className="min-w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
      >
        <option value="pending">Pending</option>
        <option value="needs_review">Needs review</option>
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
        <option value="">All statuses</option>
      </select>
    </label>
  );

  const outcomeField = setOutcomeFilter ? (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">Outcome</span>
      <select
        value={outcomeFilter ?? ''}
        onChange={(ev) => setOutcomeFilter(ev.target.value as OutcomeFilter)}
        className="min-w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
      >
        <option value="">Both</option>
        <option value="recovery">Recovery opportunity</option>
        <option value="compliance_risk">Compliance risk</option>
      </select>
    </label>
  ) : null;

  const invoiceField = setInvoiceFilter ? (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">Invoice</span>
      <select
        value={invoiceFilter ?? ''}
        onChange={(ev) => setInvoiceFilter(ev.target.value as InvoiceFilter)}
        disabled={invoiceLookupFailed}
        className="min-w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal disabled:cursor-not-allowed disabled:opacity-50"
      >
        <option value="">Any</option>
        <option value="attached">📎 Attached</option>
        <option value="complete">✓ Data complete</option>
        <option value="incomplete">? Data incomplete</option>
        <option value="unattached">Missing (to collect)</option>
        <option value="no_invoice">No invoice available</option>
      </select>
      {invoiceLookupFailed && (
        <span className="text-[11px] text-destructive">
          Invoice lookup failed — filter inactive
        </span>
      )}
    </label>
  ) : null;

  const approvalBasisField = setApprovalReasonFilter ? (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">Approval basis</span>
      <select
        value={approvalReasonFilter ?? ''}
        onChange={(ev) => setApprovalReasonFilter(ev.target.value as ApprovalReasonFilter)}
        className="min-w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
      >
        <option value="">Any basis</option>
        {APPROVAL_REASONS_ORDERED.map((reason) => (
          <option key={reason} value={reason}>
            {APPROVAL_REASON_META[reason].label}
          </option>
        ))}
      </select>
    </label>
  ) : null;

  const ruleField = (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">Rule</span>
      <div className="relative">
        <select
          value={ruleFilter}
          onChange={(ev) => setRuleFilter(ev.target.value)}
          className="min-w-48 appearance-none rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
        >
          <option value="">All rules ({totalCount})</option>
          {ruleIds.map((rid) => {
            const r = ruleById.get(rid);
            return (
              <option key={rid} value={rid}>
                {r ? `${r.aa_code} — ${r.title}` : rid}
              </option>
            );
          })}
        </select>
        {ruleFilter ? (
          <button
            type="button"
            aria-label="Clear rule filter"
            onClick={() => setRuleFilter('')}
            className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-brand-slate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : (
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-slate"
          />
        )}
      </div>
    </label>
  );

  const provinceField = (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">Province</span>
      <div className="relative">
        <select
          value={provinceFilter}
          onChange={(ev) => setProvinceFilter(ev.target.value)}
          className="min-w-32 appearance-none rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
        >
          <option value="">All provinces</option>
          {provinces.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        {provinceFilter ? (
          <button
            type="button"
            aria-label="Clear province filter"
            onClick={() => setProvinceFilter('')}
            className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-brand-slate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : (
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-slate"
          />
        )}
      </div>
    </label>
  );

  const supplierField = (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">Supplier</span>
      <div className="relative">
        <input
          type="text"
          list="findings-supplier-options-shared"
          value={supplierFilter}
          onChange={(ev) => setSupplierFilter(ev.target.value)}
          placeholder="All suppliers"
          className="min-w-64 rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
        />
        {supplierFilter ? (
          <button
            type="button"
            aria-label="Clear supplier filter"
            onClick={() => setSupplierFilter('')}
            className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-brand-slate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : (
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-slate"
          />
        )}
        <datalist id="findings-supplier-options-shared">
          {suppliers.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </div>
    </label>
  );

  const bnField = setBnFilter ? (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">BN</span>
      <div className="relative">
        <select
          value={bnFilter ?? ''}
          onChange={(ev) => setBnFilter(ev.target.value as BnFilter)}
          disabled={bnLookupFailed}
          className="min-w-32 appearance-none rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">Any</option>
          <option value="has">Has BN</option>
          <option value="missing">Missing BN</option>
        </select>
        {bnFilter ? (
          <button
            type="button"
            aria-label="Clear BN filter"
            onClick={() => setBnFilter('')}
            className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-brand-slate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : (
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-slate"
          />
        )}
      </div>
      {bnLookupFailed && (
        <span className="text-[11px] text-destructive">
          BN lookup failed — filter inactive
        </span>
      )}
    </label>
  ) : null;

  // Omitted (not just disabled) when the surface has no transaction
  // types — e.g. the supplier-scoped filter bar — so the advanced group
  // doesn't carry a dead no-op control.
  const typeField =
    transactionTypes.length > 0 ? (
      <label className="grid gap-1 text-xs text-brand-slate">
        <span className="uppercase tracking-wide">Type</span>
        <select
          value={transactionTypeFilter}
          onChange={(ev) => setTransactionTypeFilter(ev.target.value)}
          className="min-w-40 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
        >
          <option value="">All types</option>
          {transactionTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>
    ) : null;

  // Omitted (not just disabled) unless the surface wires the filter AND
  // there's something to show — blank / "-No Class-" rows never produce
  // an option, so QBO/Xero runs (no Class column) skip the control
  // entirely. A persisted classFilter that no longer matches any finding
  // still renders (with its stuck value injected as an option): otherwise
  // it would hide every row while the control that explains why is
  // itself hidden, leaving an unexplained active-filter count.
  const classOptions =
    classFilter && !(classes ?? []).includes(classFilter)
      ? [classFilter, ...(classes ?? [])]
      : (classes ?? []);
  const classField =
    setClassFilter && (classOptions.length > 0 || classFilter) ? (
      <label className="grid gap-1 text-xs text-brand-slate">
        <span className="uppercase tracking-wide">Class</span>
        <select
          value={classFilter ?? ''}
          onChange={(ev) => setClassFilter(ev.target.value)}
          className="min-w-40 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
        >
          <option value="">All classes</option>
          {classOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
    ) : null;

  const glField = setGlAccountFilter ? (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">GL account</span>
      <div className="relative">
        <input
          type="text"
          value={glAccountFilter ?? ''}
          onChange={(ev) => setGlAccountFilter(ev.target.value)}
          placeholder="6019, Severance, 2220…"
          aria-label="GL account contains"
          className="min-w-48 rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
        />
        {glAccountFilter ? (
          <button
            type="button"
            onClick={() => setGlAccountFilter('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-brand-slate hover:text-brand-royal"
            aria-label="Clear GL account filter"
            title="Clear GL account filter"
          >
            ✕
          </button>
        ) : null}
      </div>
    </label>
  ) : null;

  const currencyField =
    setCurrencyFilter && (currencies?.length ?? 0) > 0 ? (
      <label className="grid gap-1 text-xs text-brand-slate">
        <span className="uppercase tracking-wide">Currency</span>
        <div className="relative">
          <select
            value={currencyFilter ?? ''}
            onChange={(ev) => setCurrencyFilter(ev.target.value)}
            className="min-w-28 appearance-none rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          >
            <option value="">All</option>
            {currencies?.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {currencyFilter ? (
            <button
              type="button"
              aria-label="Clear currency filter"
              onClick={() => setCurrencyFilter('')}
              className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-brand-slate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            >
              <span aria-hidden="true">×</span>
            </button>
          ) : (
            <ChevronDown
              aria-hidden="true"
              className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-slate"
            />
          )}
        </div>
      </label>
    ) : null;

  const sourceField =
    setSourceFilter && (sources?.length ?? 0) > 0 ? (
      <label className="grid gap-1 text-xs text-brand-slate">
        <span className="uppercase tracking-wide">Source</span>
        <div className="relative">
          <select
            value={sourceFilter ?? ''}
            onChange={(ev) => setSourceFilter(ev.target.value)}
            className="min-w-28 appearance-none rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          >
            <option value="">All</option>
            {sources?.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          {sourceFilter ? (
            <button
              type="button"
              aria-label="Clear source filter"
              onClick={() => setSourceFilter('')}
              className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-brand-slate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            >
              <span aria-hidden="true">×</span>
            </button>
          ) : (
            <ChevronDown
              aria-hidden="true"
              className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-slate"
            />
          )}
        </div>
      </label>
    ) : null;

  const ledgerSourceField =
    setLedgerSourceFilter && (ledgerSources?.length ?? 0) > 0 ? (
      <label className="grid gap-1 text-xs text-brand-slate">
        <span className="uppercase tracking-wide">Ledger</span>
        <div className="relative">
          <select
            value={ledgerSourceFilter ?? ''}
            onChange={(ev) => setLedgerSourceFilter(ev.target.value)}
            className="min-w-28 appearance-none rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          >
            <option value="">All</option>
            {ledgerSources?.map((s) => (
              <option key={s} value={s}>
                {s === 'qbo' ? 'QBO' : s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          {ledgerSourceFilter ? (
            <button
              type="button"
              aria-label="Clear ledger filter"
              onClick={() => setLedgerSourceFilter('')}
              className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-brand-slate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            >
              <span aria-hidden="true">×</span>
            </button>
          ) : (
            <ChevronDown
              aria-hidden="true"
              className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-slate"
            />
          )}
        </div>
      </label>
    ) : null;

  const amountField =
    setAmountMin && setAmountMax ? (
      <label className="grid gap-1 text-xs text-brand-slate">
        <span className="uppercase tracking-wide">Amount (CAD)</span>
        <div className="flex items-center gap-1">
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={amountMin ?? ''}
            onChange={(ev) => setAmountMin(ev.target.value)}
            placeholder="min"
            aria-label="Minimum recovery amount"
            className="w-24 rounded-md border border-input bg-background px-2 py-1.5 text-sm tabular-nums shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <span aria-hidden="true" className="text-brand-slate">–</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={amountMax ?? ''}
            onChange={(ev) => setAmountMax(ev.target.value)}
            placeholder="max"
            aria-label="Maximum recovery amount"
            className="w-24 rounded-md border border-input bg-background px-2 py-1.5 text-sm tabular-nums shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          {(amountMin || amountMax) && (
            <button
              type="button"
              aria-label="Clear amount range"
              onClick={() => {
                setAmountMin('');
                setAmountMax('');
              }}
              className="flex h-7 w-7 items-center justify-center text-brand-slate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </div>
      </label>
    ) : null;

  const effRateField =
    setEffRateMin && setEffRateMax ? (
      <label className="grid gap-1 text-xs text-brand-slate">
        <span className="uppercase tracking-wide">Eff. rate (%)</span>
        <div className="flex items-center gap-1">
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={effRateMin ?? ''}
            onChange={(ev) => setEffRateMin(ev.target.value)}
            placeholder="min"
            aria-label="Minimum effective tax rate (percent)"
            className="w-24 rounded-md border border-input bg-background px-2 py-1.5 text-sm tabular-nums shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <span aria-hidden="true" className="text-brand-slate">–</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={effRateMax ?? ''}
            onChange={(ev) => setEffRateMax(ev.target.value)}
            placeholder="max"
            aria-label="Maximum effective tax rate (percent)"
            className="w-24 rounded-md border border-input bg-background px-2 py-1.5 text-sm tabular-nums shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          {(effRateMin || effRateMax) && (
            <button
              type="button"
              aria-label="Clear effective-rate range"
              onClick={() => {
                setEffRateMin('');
                setEffRateMax('');
              }}
              className="flex h-7 w-7 items-center justify-center text-brand-slate hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </div>
      </label>
    ) : null;

  const reasonField = setReasonCodesFilter ? (
    <label className="grid gap-1 text-xs text-brand-slate">
      <span className="uppercase tracking-wide">Reason codes</span>
      <div className="relative">
        <select
          multiple={false}
          value=""
          onChange={(ev) => {
            const code = ev.target.value as ReasonCode | '';
            if (code === '') return;
            const current = reasonCodesFilter ?? [];
            if (current.includes(code)) {
              setReasonCodesFilter(current.filter((c) => c !== code));
            } else {
              setReasonCodesFilter(
                REASON_CODES_ORDERED.filter(
                  (c) => current.includes(c) || c === code,
                ) as ReasonCode[],
              );
            }
            // Reset the select to placeholder so the user can pick again.
            ev.target.value = '';
          }}
          className="min-w-44 appearance-none rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          aria-label="Add a reason-code filter"
        >
          <option value="">
            {reasonCodesFilter && reasonCodesFilter.length > 0
              ? `${reasonCodesFilter.length} selected (any-of)`
              : 'Filter by code…'}
          </option>
          {REASON_CODES_ORDERED.map((code) => {
            const meta = REASON_CODE_META[code];
            const checked = (reasonCodesFilter ?? []).includes(code);
            return (
              <option key={code} value={code}>
                {checked ? '✓ ' : ''}
                {meta.label}
              </option>
            );
          })}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-slate"
        />
      </div>
      {(reasonCodesFilter?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1">
          {(reasonCodesFilter ?? []).map((code) => (
            <button
              key={code}
              type="button"
              onClick={() =>
                setReasonCodesFilter((reasonCodesFilter ?? []).filter((c) => c !== code))
              }
              title={`Remove ${REASON_CODE_META[code].label}`}
              className="inline-flex items-center gap-1 rounded-full bg-brand-royal/10 px-2 py-0.5 text-[11px] font-medium text-brand-royal hover:bg-brand-royal/20"
            >
              {REASON_CODE_META[code].label}
              <span aria-hidden="true">×</span>
            </button>
          ))}
        </div>
      )}
    </label>
  ) : null;

  const groupClass = 'grid gap-1.5';
  const groupLabelClass =
    'text-[11px] font-semibold uppercase tracking-wide text-brand-slate/70';
  const groupRowClass = 'flex flex-wrap items-end gap-3';

  return (
    <div className="grid gap-3 rounded-md border border-border bg-muted/20 px-3 py-3">
      {/* Primary row — the filters used on almost every review pass. */}
      <div className="flex flex-wrap items-end gap-3">
        {searchField}
        {statusField}
        {outcomeField}
        {invoiceField}
        {approvalBasisField}
        <button
          type="button"
          onClick={() => setMoreOpen((o) => !o)}
          aria-expanded={moreOpen}
          className="flex items-center gap-1.5 self-end rounded-md border border-input bg-background px-3 py-1.5 text-xs text-brand-slate transition-colors hover:border-brand-royal/50 hover:text-brand-royal"
        >
          <ChevronDown
            aria-hidden="true"
            className={cn('h-4 w-4 transition-transform', moreOpen && 'rotate-180')}
          />
          More filters
          {advancedActiveCount > 0 ? ` (${advancedActiveCount})` : ''}
        </button>
        {hasActiveFilter && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="self-end"
            onClick={onClearAll}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Advanced filters — collapsed by default, grouped by what they
          narrow so the ~10 controls are navigable instead of a wall. */}
      {moreOpen && (
        <div className="grid gap-3 border-t border-border/60 pt-3">
          <div className={groupClass}>
            <p className={groupLabelClass}>Transaction</p>
            <div className={groupRowClass}>
              {ruleField}
              {typeField}
              {classField}
              {sourceField}
              {ledgerSourceField}
              {glField}
              {currencyField}
              {amountField}
              {effRateField}
            </div>
          </div>
          <div className={groupClass}>
            <p className={groupLabelClass}>Supplier</p>
            <div className={groupRowClass}>
              {supplierField}
              {provinceField}
              {bnField}
            </div>
          </div>
          {reasonField && (
            <div className={groupClass}>
              <p className={groupLabelClass}>Rejection</p>
              <div className={groupRowClass}>{reasonField}</div>
            </div>
          )}
        </div>
      )}

      <div className="border-t border-border/60 pt-3 text-sm text-brand-slate">
        Showing {showingCount} of {totalCount} findings
      </div>
    </div>
  );
}

export { FindingFilterBar };
